import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CheckCircle2, Store, Loader2, Clock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import authBackground from "@/assets/auth-background.jpg";
import ConsultaAgendamentos from "@/components/ConsultaAgendamentos";
import ContactInfo from "@/components/ContactInfo";
import MovimentacaoDia from "@/components/MovimentacaoDia";
import { TermsNotice } from "@/components/booking/TermsNotice";

interface LojaConfig {
  name?: string;
  opening_time?: string;
  closing_time?: string;
  slot_interval_minutes?: number;
  nome_profissionais?: string;
  escolha_serviços?: string;
  instructions?: string;
}

interface SpecialInfo {
  isClosed?: boolean;
  closedMessage?: string;
  isSpecialHours?: boolean;
  specialHoursMessage?: string;
  specialHoursOpening?: string;
  specialHoursClosing?: string;
  isHoliday?: boolean;
  holiday?: { descricao: string };
}

function isValidBrazilContact(contact: string): boolean {
  const digits = contact.replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 11;
}

export default function Booking() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<LojaConfig | null>(null);
  const [session, setSession] = useState<any>(null);
  const [specialDatesList, setSpecialDatesList] = useState<any[]>([]);
  const [professional, setProfessional] = useState<string>("");
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [selectedDateCard, setSelectedDateCard] = useState<string | null>(null);
  const [slotsForSelectedDate, setSlotsForSelectedDate] = useState<string[]>([]);
  const [specialInfo, setSpecialInfo] = useState<SpecialInfo | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pros, setPros] = useState<string[]>([]);
  const [services, setServices] = useState<string[]>([]);
  const [service, setService] = useState<string>("");
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [botField, setBotField] = useState("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isFormValid = useMemo(() => {
    return isValidBrazilContact(contact) && name.trim() !== "" && selectedSlot && selectedDateCard && professional && service;
  }, [contact, name, selectedSlot, selectedDateCard, professional, service]);

  const brazilNow = useMemo(() => {
    const now = new Date();
    const date = now.toLocaleString("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit" }).split('T')[0];
    const time = now.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
    return { date, time };
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("info_loja_public").select("*").limit(1).maybeSingle();
      if (data) setConfig(data);
    })();
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => setSession(newSession));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const { data } = await supabase.from("info_loja_public").select("nome_profissionais, escolha_serviços") as any;
        if (data) {
          const allP = data.map((r: any) => r.nome_profissionais || "").join(";").split(/[;,\n]/).map((s: string) => s.trim()).filter(Boolean);
          const allS = data.map((r: any) => r.escolha_serviços || "").join(";").split(/[;,\n]/).map((s: string) => s.trim()).filter(Boolean);
          setPros([...new Set(allP)]);
          setServices([...new Set(allS)]);
        }
      } catch (err) { console.error(err); }
    };
    loadData();
  }, []);

  const nextSixDates = useMemo(() => {
    const arr: string[] = [];
    const [y, m, d] = brazilNow.date.split('-').map(Number);
    const today = new Date(y, m - 1, d);
    let i = 0;
    while (arr.length < 6) {
      const target = new Date(today);
      target.setDate(today.getDate() + i);
      if (target.getDay() !== 0) {
        arr.push(`${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, "0")}-${String(target.getDate()).padStart(2, "0")}`);
      }
      i++;
    }
    return arr;
  }, [brazilNow.date]);

  const fetchSpecialDates = useCallback(async () => {
    try {
      const { data } = await supabase.from('horarios_especiais').select('*').gte('data', nextSixDates[0]).lte('data', nextSixDates[nextSixDates.length - 1]);
      if (data) setSpecialDatesList(data);
    } catch (err) { console.error(err); }
  }, [nextSixDates]);

  useEffect(() => {
    if (nextSixDates.length > 0) fetchSpecialDates();
  }, [nextSixDates, fetchSpecialDates]);

  const fetchSlotsForDate = useCallback(async (dStr: string) => {
    if (!config?.opening_time || !config?.closing_time || !config?.slot_interval_minutes) return;
    setLoadingSlots(true);
    try {
      const { data, error } = await supabase.functions.invoke("get-available-slots", {
        body: { date: dStr, professional: professional || undefined, opening_time: config.opening_time, closing_time: config.closing_time, slot_interval_minutes: config.slot_interval_minutes }
      });
      if (error) throw error;
      setSlotsForSelectedDate(data?.slots || []);
      setSpecialInfo({
        isClosed: data?.isClosed || false, closedMessage: data?.closedMessage || null, isSpecialHours: data?.isSpecialHours || false, specialHoursMessage: data?.specialHoursMessage || null,
        specialHoursOpening: data?.specialHoursOpening || null, specialHoursClosing: data?.specialHoursClosing || null, isHoliday: data?.isHoliday || false, holiday: data?.holiday || null
      });
    } catch (err) { console.error(err); toast.error("Erro ao buscar horários."); } finally { setLoadingSlots(false); }
  }, [config, professional]);

  const handleDateCardClick = useCallback((dateStr: string) => {
    setSelectedSlot(null);
    setSelectedDateCard(dateStr);
    const special = specialDatesList.find(s => s.data === dateStr);
    if (special) {
      setSpecialInfo({
        isClosed: special.tipo === "fechado", closedMessage: special.mensagem, isSpecialHours: special.tipo === "horario_especial", specialHoursMessage: special.mensagem,
        specialHoursOpening: special.horario_abertura, specialHoursClosing: special.horario_fechamento, isHoliday: false, holiday: null
      });
      if (special.tipo === "fechado") return;
    } else { setSpecialInfo(null); }
    if (professional) fetchSlotsForDate(dateStr);
  }, [fetchSlotsForDate, professional, specialDatesList]);

  useEffect(() => {
    if (nextSixDates.length > 0 && !selectedDateCard && config && specialDatesList.length >= 0) {
      handleDateCardClick(nextSixDates[0]);
    }
  }, [nextSixDates, config, selectedDateCard, specialDatesList, handleDateCardClick]);

  useEffect(() => {
    if (selectedDateCard && professional && config) {
      setSelectedSlot(null);
      fetchSlotsForDate(selectedDateCard);
    }
  }, [professional]);

  useEffect(() => {
    if (!config || !selectedDateCard) return;
    const channelA = supabase.channel("booking-slots").on("postgres_changes", { event: "*", schema: "public", table: "agendamentos_robustos" }, (payload: any) => {
      if ((payload.new?.DATA || payload.old?.DATA) === selectedDateCard) {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => fetchSlotsForDate(selectedDateCard), 1000);
      }
    }).subscribe();
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      supabase.removeChannel(channelA);
    };
  }, [config, selectedDateCard, fetchSlotsForDate]);

  const specialInfoOverlay = useMemo(() => {
    if (!specialInfo) return null;
    if (specialInfo.isClosed) return { title: "Loja Fechada", message: specialInfo.closedMessage || "Estamos fechados nesta data.", type: "closed" as const, icon: <Store className="w-20 h-20 text-red-500 relative z-10" /> };
    if (specialInfo.isSpecialHours) {
      const hoursMsg = (specialInfo.specialHoursOpening && specialInfo.specialHoursClosing) 
        ? `Horário Especial: das ${specialInfo.specialHoursOpening.slice(0, 5)} às ${specialInfo.specialHoursClosing.slice(0, 5)}`
        : "";
      return { 
        title: "Horário Especial", 
        message: specialInfo.specialHoursMessage || "Funcionaremos em horário diferenciado.", 
        hours: hoursMsg,
        type: "special" as const, 
        icon: <Clock className="w-20 h-20 text-warning relative z-10" /> 
      };
    }
    return null;
  }, [specialInfo]);

  const handleSlotSelect = useCallback((slot: string) => {
    if (!slotsForSelectedDate.includes(slot)) { toast.error("Este horário não está mais disponível."); return; }
    setSelectedSlot(slot);
  }, [slotsForSelectedDate]);

  async function handleBook() {
    if (!name || !contact || !professional || !service || !selectedSlot || !selectedDateCard) return;
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("book-slot", { body: { date: selectedDateCard, time: selectedSlot, name, contact, professional, service, botField } });
      if (error) throw error;
      setBooking(data?.booking);
      navigate("/booking-confirmation", { state: { date: selectedDateCard, time: selectedSlot, name, contact, professional, service, senha: data?.booking?.senha } });
    } catch (e: any) {
      console.error(e);
      const serverMsg: string = e?.context?.error ?? "";
      const clientMsg: string = e?.message ?? "";

      let msg: string;
      if (serverMsg.includes("Limite") || serverMsg.includes("excedido") || clientMsg.includes("429")) {
        msg = "Você atingiu o limite de agendamentos por hora. Aguarde um pouco e tente novamente. ⏳";
      } else if (serverMsg) {
        msg = serverMsg;
      } else if (clientMsg && !clientMsg.toLowerCase().includes("edge function") && !clientMsg.includes("non-2xx")) {
        msg = clientMsg;
      } else {
        msg = "Erro ao agendar. Tente novamente em instantes.";
      }
      toast.error(msg);
    } finally { setIsSubmitting(false); }
  }

  const isClosingTimePassed = useMemo(() => {
    if (!config?.closing_time) return false;
    const [cH, cM] = brazilNow.time.split(':').map(Number);
    const [kH, kM] = config.closing_time.slice(0, 5).split(':').map(Number);
    return (cH * 60 + cM) >= (kH * 60 + kM);
  }, [config?.closing_time, brazilNow.time]);

  return (
    <div className="min-h-screen bg-cover bg-center bg-no-repeat relative" style={{ backgroundImage: `url(${authBackground})` }}>
      <div className="absolute inset-0 bg-black/50"></div>
      <main className="container mx-auto px-6 py-8 relative z-10 font-sans">
        <header className="mb-12 text-center relative">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6 mb-12">
            <div className="flex gap-4">
              <Button onClick={() => navigate("/cancel")} variant="outline" className="btn-skeuo-red uppercase font-black px-8 py-6 h-auto text-lg hover:scale-105 transition-all">CANCELAR</Button>
              <Button onClick={() => navigate("/reschedule")} variant="outline" className="btn-skeuo-orange uppercase font-black px-8 py-6 h-auto text-lg hover:scale-105 transition-all">REAGENDAR</Button>
            </div>
            <div className="flex flex-col gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate(session ? "/dashboard" : "/auth")} 
                className="btn-skeuo-dark uppercase font-black px-8 py-4 h-auto text-sm hover:scale-105 transition-all w-full flex items-center justify-center gap-2"
              >
                {session ? "DASHBOARD" : "ADMIN"}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate("/loja")} 
                className="btn-skeuo-dark uppercase font-black px-8 py-4 h-auto text-sm hover:scale-105 transition-all w-full flex items-center justify-center gap-2"
              >
                <Store className="w-4 h-4" /> NOSSA LOJA
              </Button>
            </div>
          </div>
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="space-y-4">
            <h1 className="text-6xl sm:text-8xl font-black text-primary uppercase tracking-tighter leading-none decoration-orange-500/50 underline-offset-8 underline">{config?.name || "AGENDAR"}</h1>
            <p className="text-xl text-slate-400 font-bold uppercase tracking-[0.3em]">CUIDADOS DE ALTA PERFORMANCE</p>
          </motion.div>
        </header>

        <section className="grid gap-8 md:grid-cols-2 mb-12">
          <ConsultaAgendamentos />
          <MovimentacaoDia />
        </section>

        <Card className="mb-8 bg-black/40 backdrop-blur-xl border-white/5 shadow-2xl overflow-hidden">
          <CardHeader className="bg-white/5 border-b border-white/5"><CardTitle className="text-primary uppercase font-black tracking-widest">Escolha sua reserva</CardTitle></CardHeader>
          <CardContent className="p-8">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {nextSixDates.map(d => {
                const dateObj = new Date(d + 'T12:00:00');
                const isSelected = selectedDateCard === d;
                const isTodayAndClosed = (d === brazilNow.date && isClosingTimePassed);
                return (
                  <Card key={d} className={cn("cursor-pointer transition-all border-0 rounded-2xl bg-zinc-900 shadow-[0_6px_0_rgba(0,0,0,1)] active:translate-y-1 active:shadow-none", isSelected && "ring-4 ring-primary bg-primary/10", isTodayAndClosed && "opacity-30 grayscale pointer-events-none")} onClick={() => handleDateCardClick(d)}>
                    <CardContent className="p-4 text-center">
                      <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-1">{format(dateObj, "EEE", { locale: ptBR })}</p>
                      <p className={cn("text-3xl font-black", isSelected ? "text-primary" : "text-white")}>{format(dateObj, "dd", { locale: ptBR })}</p>
                      <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest mt-1">{format(dateObj, "MMM", { locale: ptBR })}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="relative min-h-[400px]">
          <AnimatePresence>
            {specialInfoOverlay && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={cn("absolute inset-0 z-50 rounded-[3.5rem] flex flex-col items-center justify-center p-12 text-center overflow-hidden", specialInfoOverlay.type === "closed" ? "bg-red-500/20 border-4 border-red-500/30 backdrop-blur-3xl" : "bg-warning/10 border-2 border-warning/20 backdrop-blur-2xl")}>
                <div className={cn("absolute inset-0 opacity-10", specialInfoOverlay.type === "closed" ? "bg-red-500" : "bg-warning")} />
                <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="space-y-10 max-w-xl relative z-10">
                  <div className="flex justify-center"><div className={cn("p-12 rounded-full border-4 shadow-2xl", specialInfoOverlay.type === "closed" ? "bg-red-500/20 border-red-500/40 text-red-500" : "bg-warning/20 border-warning/40 text-warning")}>{specialInfoOverlay.icon}</div></div>
                  <div className="space-y-6">
                    <h2 className={cn("text-6xl sm:text-7xl font-black uppercase tracking-tighter leading-none decoration-orange-500/50 underline-offset-8 underline", specialInfoOverlay.type === "closed" ? "text-white" : "text-warning")}>{specialInfoOverlay.title}</h2>
                    <div className="space-y-3">
                      <p className="text-2xl sm:text-3xl text-slate-100 font-bold leading-tight">{specialInfoOverlay.message}</p>
                      {specialInfoOverlay.hours && (
                        <div className="inline-block px-6 py-2 bg-warning/20 border border-warning/30 rounded-full">
                          <p className="text-xl text-warning font-black uppercase tracking-widest">{specialInfoOverlay.hours}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-6 justify-center w-full">
                    {specialInfoOverlay.type === "closed" ? (
                      <div className="flex flex-col gap-4 w-full">
                        <div className="flex flex-col sm:flex-row gap-4 w-full">
                          <Button onClick={() => navigate("/reschedule")} className="flex-1 h-20 bg-zinc-900 border border-white/10 text-white font-black rounded-2xl shadow-[0_8px_0_#000] active:translate-y-2 active:shadow-none transition-all text-xl uppercase tracking-widest">REAGENDAR</Button>
                          <Button onClick={() => navigate("/cancel")} className="flex-1 h-20 bg-zinc-900 border border-white/10 text-white font-black rounded-2xl shadow-[0_8px_0_#000] active:translate-y-2 active:shadow-none transition-all text-xl uppercase tracking-widest">CANCELAR</Button>
                        </div>
                        <Button onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); navigate("/"); }} variant="outline" className="btn-skeuo-red w-full h-20 text-2xl font-black uppercase rounded-2xl">VOLTAR AO INÍCIO</Button>
                      </div>
                    ) : ( 
                      <Button onClick={() => setSpecialInfo(null)} className="btn-skeuo-orange px-16 h-22 text-white font-black rounded-2xl text-2xl uppercase tracking-tighter w-full">CONTINUAR</Button>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className={cn("transition-all duration-700 pb-20", specialInfoOverlay && "opacity-10 grayscale pointer-events-none blur-md shrink-0")}>
            <div className="grid gap-8 md:grid-cols-2">
              <Card className={cn("bg-black/30 border-white/5 transition-all rounded-3xl", professional && "ring-2 ring-primary bg-primary/5")}>
                <CardHeader><CardTitle className={cn("uppercase font-black text-sm tracking-[0.2em]", professional && "text-primary")}>ESCOLHA O PROFISSIONAL</CardTitle></CardHeader>
                <CardContent>
                  <Select value={professional || undefined} onValueChange={setProfessional}>
                    <SelectTrigger className="bg-zinc-900 border-0 h-16 rounded-2xl text-xl font-bold"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                    <SelectContent>{pros.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </CardContent>
              </Card>
              <Card className={cn("bg-black/30 border-white/5 transition-all rounded-3xl", service && "ring-2 ring-primary bg-primary/5")}>
                <CardHeader><CardTitle className={cn("uppercase font-black text-sm tracking-[0.2em]", service && "text-primary")}>ESCOLHA O SERVIÇO</CardTitle></CardHeader>
                <CardContent>
                  <Select value={service || undefined} onValueChange={setService}>
                    <SelectTrigger className="bg-zinc-900 border-0 h-16 rounded-2xl text-xl font-bold"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                    <SelectContent>{services.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </CardContent>
              </Card>
            </div>

            <Card className="mt-12 bg-black/40 border-white/5 rounded-[3rem] shadow-2xl">
              <CardHeader className="text-center pt-10"><CardTitle className="text-4xl font-black uppercase tracking-tighter text-primary">Horários Disponíveis</CardTitle></CardHeader>
              <CardContent className="p-10">
                {!selectedDateCard || !professional ? (
                  <div className="py-20 text-center opacity-30"><Clock className="w-20 h-20 mx-auto mb-6" /><p className="text-2xl font-black uppercase tracking-widest">Aguardando seleções...</p></div>
                ) : loadingSlots ? (
                  <div className="py-20 flex flex-col items-center gap-6"><Loader2 className="h-16 w-16 animate-spin text-primary" /><p className="text-xl font-bold uppercase tracking-widest text-slate-400">Consultando agenda em tempo real...</p></div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                    {slotsForSelectedDate.map(s => (
                      <button key={s} onClick={() => handleSlotSelect(s)} className={cn("h-16 rounded-2xl font-black text-xl transition-all border-b-8 active:translate-y-2 active:border-b-0", selectedSlot === s ? "bg-primary text-black border-orange-700 shadow-[0_12px_30px_rgba(245,158,11,0.4)]" : "bg-zinc-900 text-slate-500 border-black hover:border-primary/50 hover:text-white")}>{s}</button>
                    ))}
                    {slotsForSelectedDate.length === 0 && <p className="col-span-full py-12 text-center text-slate-500 text-2xl font-black uppercase opacity-20">Nenhum horário disponível</p>}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mt-12 bg-black/30 border-white/5 rounded-[3rem]">
              <CardHeader className="text-center pt-8"><CardTitle className="uppercase font-black tracking-widest text-slate-500">Seus dados para finalize</CardTitle></CardHeader>
              <CardContent className="p-10 grid gap-8 md:grid-cols-2">
                {/* Honeypot Input: Hidden from real users */}
                <input type="text" style={{ display: 'none' }} value={botField} onChange={e => setBotField(e.target.value)} tabIndex={-1} aria-hidden="true" autoComplete="off" />
                <div className="space-y-3"><Label className="text-slate-400 font-bold uppercase ml-2">NOME COMPLETO</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Como gostaria de ser chamado?" className="bg-zinc-900 border-0 h-16 rounded-2xl text-xl px-6 focus:ring-4 ring-primary/20 transition-all font-bold" /></div>
                <div className="space-y-3"><Label className="text-slate-400 font-bold uppercase ml-2">CONTATO</Label><Input value={contact} onChange={e => setContact(e.target.value)} placeholder="(21) 99876-5432" className={cn("bg-zinc-900 border-0 h-16 rounded-2xl text-xl px-6 transition-all font-bold", contact && !isValidBrazilContact(contact) && "ring-2 ring-red-500")} /></div>
                
                <div className="col-span-full mt-2">
                  <TermsNotice />
                </div>
              </CardContent>
            </Card>

            <Button onClick={handleBook} disabled={!isFormValid || isSubmitting} className={cn("w-full mt-12 h-28 text-4xl font-black transition-all rounded-[2.5rem] uppercase tracking-tighter", isFormValid && !isSubmitting ? "bg-primary text-white shadow-[0_15px_60px_rgba(245,158,11,0.4)] hover:shadow-[0_25px_80px_rgba(245,158,11,0.6)] hover:scale-[1.02] active:scale-95 [text-shadow:0_3px_5px_rgba(0,0,0,0.8),0_0_15px_rgba(255,255,255,0.4)]" : "bg-zinc-800 text-zinc-600 grayscale")}>
              {isSubmitting ? <><Loader2 className="mr-6 h-12 w-12 animate-spin" />AGUARDE...</> : "AGENDAR"}
            </Button>
          </div>
        </div>
        <div className="max-w-2xl mx-auto mt-20"><ContactInfo /></div>
      </main>

      {booking && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/90 backdrop-blur-3xl">
          <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-2xl bg-zinc-900 border-2 border-green-500/40 rounded-[3.5rem] p-12 text-center shadow-[0_0_150px_rgba(34,197,94,0.3)]">
            <div className="p-8 rounded-full bg-green-500/10 w-fit mx-auto mb-8 border-2 border-green-500/20"><CheckCircle2 className="w-20 h-20 text-green-500" /></div>
            <h2 className="text-5xl font-black text-white uppercase tracking-tighter mb-4">AGENDADO COM SUCESSO!</h2>
            <p className="text-2xl text-slate-400 mb-10 font-bold">Reserva confirmada em sua agenda.</p>
            <div className="grid gap-6 mb-12">
              <div className="p-6 bg-white/5 rounded-3xl"><p className="text-slate-500 font-black uppercase text-sm mb-2">PROFISSIONAL</p><p className="text-3xl font-black text-white">{booking.PROFISSIONAL}</p></div>
              <div className="p-6 bg-white/5 rounded-3xl"><p className="text-slate-500 font-black uppercase text-sm mb-2">HORÁRIO</p><p className="text-4xl font-black text-warning leading-none">{format(new Date(booking.DATA + 'T12:00:00'), "PPP", { locale: ptBR })}<br/><span className="text-5xl">{String(booking.HORA).slice(0, 5)}</span></p></div>
            </div>
            <Button onClick={() => setBooking(null)} className="w-full h-24 bg-green-600 hover:bg-green-500 text-white font-black text-2xl rounded-3xl shadow-[0_10px_0_#166534] active:translate-y-2 active:shadow-none transition-all uppercase tracking-widest">FINALIZAR</Button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
